import type { FC, ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { PlainTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { StaticListType } from "app/pages/sales-intelligence/domain/lists/types/static-list-model";
import { EnrichmentDropdown } from "app/pages/sales-intelligence/modules/enrichment/components/EnrichmentDropdown/EnrichmentDropdown";
import { UploadEnrichmentListModal } from "app/pages/sales-intelligence/modules/enrichment/components/UploadEnrichmentListModal/UploadEnrichmentListModal";
import useUploadCSVEnrichmentFile from "app/pages/sales-intelligence/modules/enrichment/hooks/useUploadCSVEnrichmentFile";
import useEnrichmentList from "app/pages/sales-intelligence/modules/enrichment/queries/useEnrichmentList";
import { EnrichmentStatus } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import { CSV_ENRICHMENT_BTN } from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/main.texts";
import { StyledResultsContainer } from "app/pages/sales-intelligence/pages/home/components/styles";
import EmptyCSVEnrichmentView from "app/pages/sales-intelligence/pages/home/containers/CSVEnrichmentContainer/EmptyCSVEnrichmentView";
import NewAssetIconButtonTracked from "app/pages/sales-intelligence/pages/home/containers/NewAssetButton/NewAssetIconButtonTracked";
import TutorialViewTracked from "app/pages/sales-intelligence/pages/home/containers/TutorialView/TutorialViewTracked";
import type { ViewModelMetadata } from "app/pages/sales-intelligence/pages/home/types/models";
import { MAX_WATCH_LIST_TOOLTIP_WIDTH } from "app/pages/sales-intelligence/pages/home/utils/const";
import { CardEventCategory } from "app/pages/sales-intelligence/services/tracking/home-page/types";
import { fetchExcelQuotaThunk } from "app/pages/sales-intelligence/sub-modules/common/store/effects";

import HomeDataCardTracked from "../HomeDataCard/HomeDataCardTracked";

import {
    StyledCsvEnrichmentContent,
    StyledCsvEnrichmentHeaderContainer,
    StyledCsvEnrichmentIconButtonWrapper,
} from "./styles";

const CARD_ID = "csv-enrichment";

type CSVEnrichmentContainerProps = {
    lists: readonly StaticListType[];
};

const CSVEnrichmentContainer: FC<CSVEnrichmentContainerProps> = ({ lists }) => {
    const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
    const dispatch = useDispatch();
    const { file, setFile, handleFileChange } = useUploadCSVEnrichmentFile();

    const { data } = useEnrichmentList();
    const validData = useMemo(() => data?.filter((item) => item.status === EnrichmentStatus.Succeeded), [data]);

    const hasCsvFiles = validData && validData.length > 0;

    const metadata: ViewModelMetadata = {
        trackingCategory: CardEventCategory.CsvEnrichment,
        totalResultsCount: validData?.length ?? 0,
    };

    useEffect(() => {
        void dispatch(fetchExcelQuotaThunk());
    }, []);

    const renderHeaderActions = (): ReactElement | null => {
        if (!hasCsvFiles) {
            return null;
        }

        return (
            <StyledCsvEnrichmentHeaderContainer>
                <EnrichmentDropdown
                    trackingCategory={metadata.trackingCategory}
                    enrichmentButton={
                        <NewAssetIconButtonTracked
                            key="upload-button"
                            metadata={metadata}
                            iconName="chev-down"
                            placement="right"
                        >
                            {translate(CSV_ENRICHMENT_BTN)}
                        </NewAssetIconButtonTracked>
                    }
                    trackingResultList={lists}
                    onUploadCsv={() => setIsCsvModalOpen(true)}
                />

                <StyledCsvEnrichmentIconButtonWrapper>
                    <PlainTooltip
                        tooltipContent={translate("si.pages.home.card.tooltip.watch_tutorial")}
                        placement="top"
                        maxWidth={MAX_WATCH_LIST_TOOLTIP_WIDTH}
                    >
                        <div>
                            <TutorialViewTracked
                                metadata={metadata}
                                stateId="salesIntelligence-home-csvEnrichment"
                            />
                        </div>
                    </PlainTooltip>
                </StyledCsvEnrichmentIconButtonWrapper>
            </StyledCsvEnrichmentHeaderContainer>
        );
    };

    return (
        <HomeDataCardTracked
            automationId={CARD_ID}
            title={{ key: `si.pages.home.card.${CARD_ID}.title` }}
            iconName="green-csv-outline"
            headerContent={renderHeaderActions()}
            metadata={metadata}
        >
            {hasCsvFiles ? (
                <StyledCsvEnrichmentContent>
                    {translate("si.pages.home.card.csv-enrichment.description")}
                </StyledCsvEnrichmentContent>
            ) : (
                <StyledResultsContainer>
                    <EmptyCSVEnrichmentView
                        automationId={CARD_ID}
                        metadata={metadata}
                        onUploadCsv={() => setIsCsvModalOpen(true)}
                    />
                </StyledResultsContainer>
            )}

            <UploadEnrichmentListModal
                isOpen={isCsvModalOpen}
                onOpenStateChange={setIsCsvModalOpen}
                file={file}
                setFile={setFile}
                onFileChange={handleFileChange}
            />
        </HomeDataCardTracked>
    );
};

export default CSVEnrichmentContainer;
